import { BaseSignatureInserter } from "./BaseSignatureInserter";
import { info, warn, error, debug } from "@/shared/services/logger";
import type { InsertResult } from "@/shared/types/inserter";

const SIGNATURE_START_MARKER = "<!-- HELPITECH_SIG_START -->";
const SIGNATURE_END_MARKER = "<!-- HELPITECH_SIG_END -->";

/**
 * Implementa inserção de assinatura para o Outlook Novo (Web/New UI),
 * usando `setSignatureAsync` como método preferencial com fallback
 * para `prependAsync`.
 *
 * A lógica compartilhada de orquestração fica em {@link BaseSignatureInserter}.
 *
 * @example
 * ```typescript
 * const inserter = new OutlookNewSignatureInserter();
 * Office.actions.associate("checkSignature", (event) => inserter.insert(event));
 * ```
 */
export class OutlookNewSignatureInserter extends BaseSignatureInserter {
  constructor() {
    super("OutlookNewInserter");
  }

  /**
   * Insere HTML no corpo do item, escolhendo automaticamente o método adequado:
   * - `setSignatureAsync` para mensagens (preferencial)
   * - `setAsync` para compromissos
   * - `prependAsync` como fallback
   *
   * @param html - Conteúdo HTML da assinatura.
   * @param item - Item do Outlook.
   * @returns Promise com `InsertResult` detalhando sucesso/falha e método usado.
   *
   * @example
   * const result = await inserter.insertSignature(html, item);
   */
  insertSignature(html: string, item: Office.Item): Promise<InsertResult> {
    return new Promise((resolve) => {
      const body = (item as any).body;
      const isAppointment = (item as any).itemType === Office.MailboxEnums.ItemType.Appointment;

      if (isAppointment) {
        info(this.source, `[INSERT] Compromisso → setAsync (${html.length} chars).`);
        body.setAsync(html, { coercionType: Office.CoercionType.Html }, (r: Office.AsyncResult<void>) => {
          if (r.status === Office.AsyncResultStatus.Succeeded) {
            info(this.source, "[INSERT] setAsync OK.");
            resolve({ success: true, method: "setAsync" });
          } else {
            error(this.source, `[INSERT] setAsync falhou — code=${r.error?.code}, msg=${r.error?.message}`);
            resolve({ success: false, method: "setAsync", errorCode: r.error?.code, errorMessage: r.error?.message });
          }
        });
        return;
      }

      if (typeof body.setSignatureAsync === "function") {
        const markedHtml = "<br/>" + SIGNATURE_START_MARKER + html + SIGNATURE_END_MARKER;
        info(this.source, `[INSERT] setSignatureAsync → método preferencial (${html.length} chars).`);
        body.setSignatureAsync(markedHtml, { coercionType: Office.CoercionType.Html }, (r: Office.AsyncResult<void>) => {
          if (r.status === Office.AsyncResultStatus.Succeeded) {
            info(this.source, "[INSERT] setSignatureAsync OK.");
            resolve({ success: true, method: "setSignatureAsync" });
            return;
          }
          warn(this.source, `[INSERT] setSignatureAsync falhou (code=${r.error?.code}) → fallback prependAsync.`);
          const fallbackHtml = "<br/>" + SIGNATURE_START_MARKER + html + SIGNATURE_END_MARKER;
          body.prependAsync(fallbackHtml + "<br/>", { coercionType: Office.CoercionType.Html }, (r2: Office.AsyncResult<void>) => {
            if (r2.status === Office.AsyncResultStatus.Succeeded) {
              info(this.source, "[INSERT] prependAsync OK (fallback).");
              resolve({ success: true, method: "prependAsync" });
            } else {
              error(this.source, `[INSERT] prependAsync falhou — code=${r2.error?.code}, msg=${r2.error?.message}`);
              resolve({ success: false, method: "prependAsync", errorCode: r2.error?.code, errorMessage: r2.error?.message });
            }
          });
        });
      } else {
        info(this.source, `[INSERT] setSignatureAsync indisponível → prependAsync direto (${html.length} chars).`);
        const directHtml = "<br/>" + SIGNATURE_START_MARKER + html + SIGNATURE_END_MARKER;
        body.prependAsync(directHtml + "<br/>", { coercionType: Office.CoercionType.Html }, (r: Office.AsyncResult<void>) => {
          if (r.status === Office.AsyncResultStatus.Succeeded) {
            info(this.source, "[INSERT] prependAsync OK.");
            resolve({ success: true, method: "prependAsync" });
          } else {
            error(this.source, `[INSERT] prependAsync falhou c code=${r.error?.code}, msg=${r.error?.message}`);
            resolve({ success: false, method: "prependAsync", errorCode: r.error?.code, errorMessage: r.error?.message });
          }
        });
      }
    });
  }
}